import * as React from "react";
import { observer } from "mobx-react";

import { UitkHeading } from "uitk-react-text";

import { FlightsReviewProps } from "./typings";
import IsomorphicCarousel from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { UitkExpandoPeek } from "uitk-react-expando";
import { useLocalization } from "@shared-ui/localization-context";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";
import {
  FlightReviewModel,
  FlightsReviewFlexModuleResult,
} from "typings/microserviceModels/flights-review-flex-module";
import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { withStores } from "stores";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";

export const FlightsReview = withStores("flexModuleModelStore")(
  observer((props: FlightsReviewProps) => {
    const { flexTemplateNode, flexModuleModelStore, context } = props;
    const { id, options } = flexTemplateNode;

    const model = flexModuleModelStore.getModel(id) as FlightsReviewFlexModuleResult | null;
    const keyHelper = new ItemKeyHelper("flights-review");

    if (!model || !model.flightsReviews || model.flightsReviews.length === 0) {
      return null;
    }

    const { title, flightsReviews } = model;
    const { formatText } = useLocalization();
    const visibleCards = 3;
    const largeGridView = flightsReviews.length === visibleCards;
    const mediumGridView = flightsReviews.length < visibleCards;
    const allView = flightsReviews.length > visibleCards;

    const carousel = (
      <UitkSpacing padding={{ blockstart: "two" }}>
        <IsomorphicCarousel
          itemsVisible={{ sm: 1, md: 2, lg: 3 }}
          pageBy={1}
          peekWidth={11}
          buttonText={{
            nextButton: formatText("carousel.item.next"),
            prevButton: formatText("carousel.item.prev"),
          }}
        >
          {flightsReviews.map((reviewItem, reviewIndex) => (
            <UitkLayoutGridItem key={keyHelper.next()}>
              {FlightsReviewCarouselCard(reviewItem, reviewIndex)}
            </UitkLayoutGridItem>
          ))}
        </IsomorphicCarousel>
      </UitkSpacing>
    );

    return (
      <LazyLoad context={context}>
        <UitkSpacing padding={{ block: "six" }}>
          <div
            className="FlightsReview all-cell-1-1"
            id={id}
            data-fm={options.fmId}
            data-fm-title-id={options.fmTitleId}
          >
            <div className="carouselMediumContainer">
              <UitkHeading tag="h2" size={4}>
                {title}
              </UitkHeading>
              {allView && carousel}
              {largeGridView && (
                <Viewport>
                  <ViewSmall>{carousel}</ViewSmall>

                  <ViewMedium>{carousel}</ViewMedium>
                  <ViewLarge>
                    <UitkLayoutGrid columns={3} space="three">
                      {flightsReviews.map((reviewItem, reviewIndex) => (
                        <UitkLayoutGridItem key={keyHelper.next()}>
                          {FlightsReviewCarouselCard(reviewItem, reviewIndex)}
                        </UitkLayoutGridItem>
                      ))}
                    </UitkLayoutGrid>
                  </ViewLarge>
                </Viewport>
              )}
              {mediumGridView && (
                <Viewport>
                  <ViewSmall>{carousel}</ViewSmall>
                  <ViewMedium>
                    <UitkLayoutGrid columns={2} space="three">
                      {flightsReviews.map((reviewItem, reviewIndex) => (
                        <UitkLayoutGridItem key={keyHelper.next()}>
                          {FlightsReviewCarouselCard(reviewItem, reviewIndex)}
                        </UitkLayoutGridItem>
                      ))}
                    </UitkLayoutGrid>
                  </ViewMedium>
                  <ViewLarge>
                    <UitkLayoutGrid columns={3} space="three">
                      {flightsReviews.map((reviewItem, reviewIndex) => (
                        <UitkLayoutGridItem key={keyHelper.next()}>
                          {FlightsReviewCarouselCard(reviewItem, reviewIndex)}
                        </UitkLayoutGridItem>
                      ))}
                    </UitkLayoutGrid>
                  </ViewLarge>
                </Viewport>
              )}
            </div>
          </div>
        </UitkSpacing>
      </LazyLoad>
    );
  })
);

const ReviewText = ({ reviewText }: { reviewText: string }) => {
  const { formatText } = useLocalization();

  return (
    <UitkSpacing padding={{ block: "six" }}>
      <div>
        <UitkExpandoPeek
          className="uitk-type-300 textHeight"
          collapseLabel={formatText("flights.review.viewLess")}
          expandLabel={formatText("flights.review.viewMore")}
          collapseHeight={62}
          threshold={70}
        >
          <p>{reviewText}</p>
        </UitkExpandoPeek>
      </div>
    </UitkSpacing>
  );
};

function FlightsReviewCarouselCard(reviewItem: FlightReviewModel, index: number) {
  const { formatText, formatDate } = useLocalization();

  return (
    <UitkCard className="FlightsReviewCard" padded border>
      <UitkSpacing padding="three">
        <UitkCardContentSection>
          <UitkLayoutGrid columns={1} space="two">
            <UitkLayoutGridItem colSpan="all">
              <div className="uitk-type-200">
                {formatDate(new Date(reviewItem.publishedDate), { skeleton: "yMMMd" })}
              </div>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan="all">
              <ReviewText reviewText={reviewItem.comment} />
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan="all">
              <div className="uitk-type-300 uitk-type-bold">{formatText("flights.review.noNameAuthorText")}</div>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem colSpan="all">
              <div
                className="uitk-type-300"
                dangerouslySetInnerHTML={{
                  __html: formatText(
                    "flights.review.originDestCityAirline",
                    reviewItem.originName ? reviewItem.originName : "",
                    reviewItem.destinationName ? reviewItem.destinationName : "",
                    reviewItem.airlineName ? (reviewItem.airlineName.name ? reviewItem.airlineName.name : "") : ""
                  ),
                }}
              />
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
        </UitkCardContentSection>
      </UitkSpacing>
    </UitkCard>
  );
}

export default FlightsReview;
